import GLOBAL from "context/global";
import axios from "../axios";
import assert from "utils/assert";

/**
 * @typedef {Object} Pageable
 * @property {Object[]} content - The page content
 * @property {Object} pageable - Pageable information
 * @property {Object} pageable.sort - Sort information
 * @property {boolean} pageable.sort.empty - Whether sort is empty
 * @property {boolean} pageable.sort.sorted - Whether content is sorted
 * @property {boolean} pageable.sort.unsorted - Whether content is unsorted
 * @property {number} pageable.offset - The offset of the page
 * @property {number} pageable.pageNumber - The page number (0-based)
 * @property {number} pageable.pageSize - Size of the page
 * @property {boolean} pageable.paged - Whether the response is paged
 * @property {boolean} pageable.unpaged - Whether the response is unpaged
 * @property {boolean} last - Whether this is the last page
 * @property {number} totalElements - Total number of elements
 * @property {number} totalPages - Total number of pages
 * @property {number} size - Size of the page
 * @property {number} number - Current page number (0-based)
 * @property {Object} sort - Sort information
 * @property {boolean} sort.empty - Whether sort is empty
 * @property {boolean} sort.sorted - Whether content is sorted
 * @property {boolean} sort.unsorted - Whether content is unsorted
 * @property {boolean} first - Whether this is the first page
 * @property {number} numberOfElements - Number of elements in current page
 * @property {boolean} empty - Whether the page is empty
 */

/**
 * Base for RESTful services -- see IssbRestfulController.java for the server side implementation
 */
class RestfulService {
  constructor(path) {
    this.path = path;
    this.axios = axios;
  }

  /**
   * @param {Object} params - The parameters for the request
   * @param listMarker {string} listMarker - The marker to use for list endpoint (default: /list)
   * @param {string?} params.query - String query that some of our endpoints support. Check java code for details.
   * @param {string[]?} params.ids - Array of ids to retrieve.
   * @param {string} params.page - The page number to retrieve
   * @param {string} params.size - The number of items per page
   * @param {string} params.sort - The field to sort by, e.g. createdDate
   * @param {"asc" | "desc"} params.direction - The direction to sort by.
   * Other parameters are passed as extra url params to the server
   * @returns {Promise<Pageable>} Note Pageable! Use `await .content` to get the actual (one page of) data.
   */
  async list(params, customPath = "") {
    assert(GLOBAL.accessToken, "accessToken is required", this.path);
    // id lists can be too long for GET, esp when UUIDs are used - so send them as a post body
    const idParam = params?.ids?.join(",");
    if (idParam) {
      const postBody = {
        ids: params.ids,
      };
      params = { ...params };
      delete params.ids;
      // Note: the post endpoint has a /list marker to avoid confusion with update
      return axios.post(this.path + "/list", postBody, { params }).then(({ data }) => data);
    }
    // normal GET
    return axios.get(this.path + customPath, { params }).then(({ data }) => data);
  }

  /**
   * @param {!string} id
   * @returns {Promise<T>}
   */
  async get(id) {
    assert(GLOBAL.accessToken, "accessToken is required", this.path);
    return axios.get(`${this.path}/${id}`).then(({ data }) => data);
  }

  async create(data) {
    return axios.post(this.path, data).then(({ data }) => data);
  }

  /**
   *
   * @param {!string} id
   * @param {!Object} data
   * @param {?AxiosRequestConfig} config e.g. {headers, params, ...}
   * Also takes `merge:true` which (if set) is sent as a parameter to the server
   * @returns
   */
  async update(id, data, config) {
    assert(typeof id === "string" || typeof id === "number", "id must be a string or number", id);
	let url = `${this.path}/${id}`;
	if (config?.merge) {
		url += "?merge=true";
	}
    return axios.post(url, data, config).then(({ data }) => data);
  }

  /**
   * This will actually archive the object, rather than delete it (see IssbRestfulController)
   * @param {!string} id
   * @param {boolean} deletePermanently - If true (rarely used), actually delete the object
   * @returns {Promise<T>}
   */
  async delete(id, deletePermanently = false) {
    return axios.delete(`${this.path}/${id}`, { params: { deletePermanently } }).then(({ data }) => data);
  }
}

export default RestfulService;
